<script setup lang="ts">
import type { BackgroundType, MessageVariety, SizesType } from '@ui/styles'
import type { Placements } from '@ui/utils/placements'
import type { InputMessageValue } from './InputMessageTypes'
import SkeletonLine from '@ui/components/skeleton/SkeletonLine.vue'
import { Background, Sizes } from '@ui/styles'
import FieldTip from '../tips/FieldTip.vue'
import InputMessage from './InputMessage.vue'

withDefaults(
  defineProps<{
    label?: string
    tooltip?: string
    message?: InputMessageValue
    variety?: MessageVariety
    disabled?: boolean
    readonly?: boolean
    size?: SizesType
    background?: BackgroundType
    open?: Placements | boolean
    required?: boolean
    loading?: boolean
  }>(),
  {
    label: '',
    tooltip: '',
    message: '',
    disabled: false,
    readonly: false,
    size: Sizes.Luke,
    background: Background.Gray,
    open: false,
    required: false,
    loading: false,
  },
)

defineEmits(['click:input'])

defineSlots<{
  default?: () => void
  additional?: () => void
}>()

const border = ref()

defineExpose({
  border,
})
</script>

<template lang="pug">
.ui-input.flex.col-4(:class="[{ disabled, loading, readonly, open }, !loading && variety, background, size]")
  .flex-between-center.h-18.row-16(v-if="label || tooltip")
    template(v-if="loading")
      SkeletonLine.h-12.max-w-160
    template(v-else)
      .label.typ-12-18-500(v-if="label") {{ $t(label) }}
        span.required(v-if="required") *
      FieldTip.p-1(v-if="tooltip" :label="tooltip")
  template(v-if="loading")
    SkeletonLine.input-skeleton
  template(v-else)
    .input-container.flex.overflow-hidden(ref="border" @click="$emit('click:input')")
        slot
    InputMessage.message(:variety="variety" :message="message")
    slot(name="additional")
</template>

<style scoped>
.label {
  color: var(--cue-content-neutral-secondary);
  min-width: 0;
}

.ui-input {
  .input-container {
    background-color: var(--cue-bg-surface-primary);
    color: var(--cue-content-neutral-primary);
    transition: 0.3s ease;
    transition-property: color, background-color, outline-color, outline-width, border;
    outline: none;

    &:focus-within {
      outline-offset: 2px;
    }
  }

  &.luke {
    .input-container {
      --uno: br-16 row-16 min-h-48;
    }

    &.single-line .input-container,
    .input-skeleton {
      height: 48px;
    }
  }

  &.hanSolo {
    .input-container {
      --uno: br-14 row-12 min-h-40;
    }

    &.single-line .input-container,
    .input-skeleton {
      height: 40px;
    }
  }

  &.leia {
    .input-container {
      --uno: br-12 row-12 min-h-36;
    }

    &.single-line .input-container,
    .input-skeleton {
      height: 36px;
    }
  }

  &.r2d2 {
    .input-container {
      --uno: br-10 row-12 min-h-28;
    }

    &.single-line .input-container,
    .input-skeleton {
      height: 28px;
    }
  }

  &.gray {
    .input-container {
      border: 1px solid transparent;
      background-color: var(--cue-bg-neutral-secondary);
    }
  }

  &.white {
    .input-container {
      border: 1px solid var(--cue-border-neutral-secondary);
      background-color: var(--cue-bg-surface-primary);
    }
  }

  &.outline {
    .input-container {
      border: 1px solid var(--cue-border-neutral-secondary);
      background-color: unset;
    }
  }

  .input-container {
    background-color: var(--cue-bg-surface-primary);
    color: var(--cue-content-neutral-primary);
  }

  &.error {
    .input-container {
      border-color: var(--cue-border-error-default);
    }
  }

  &:not(.disabled) {
    .input-container {
      &:hover {
        border-color: var(--cue-border-neutral-tertiary-hover);
      }
    }
    &.gray {
      .input-container {
        &:hover {
          border-color: var(--cue-border-neutral-secondary-hover);
        }
      }
    }
  }

  &.loading {
    .input-container {
      border: none;
    }
  }

  &.disabled {
    .label,
    .message,
    .input-container {
      color: var(--cue-content-neutral-disabled);
    }
  }
}

.required {
  display: inline;
  margin-left: 4px;
  user-select: none;
}
</style>
